import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { selectShouldRender } from 'yoda-site-components/lib/selectors/ContextSelector';
import RedirectAction from 'yoda-app-shell/lib/action/RedirectAction';
import IrisActions from 'yoda-site-components/lib/actions/IrisAction';
import Layout from 'yoda-site-components/lib/components/Layout/Layout';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import ProductConstants from 'yoda-site-components/lib/common/ProductConstants';
import { CMSRenderer, TokenProvider, useLivePreviewScript } from 'yoda-site-components/lib/cms-renderer';
import ContentstackReducer from 'yoda-site-components/lib/reducers/ContentstackReducer';
import ContentstackSaga from 'yoda-site-components/lib/sagas/ContentstackSaga';
import ContentstackActions from 'yoda-site-components/lib/cms-renderer/ContentstackActions';
import withReducerSaga from 'yoda-site-components/lib/hoc/ReducerRegistry/withReducerSaga';
import useAnalytics from 'yoda-site-components/lib/cms-renderer/hooks/useAnalytics';
import 'yoda-site-components/lib/cms-renderer/tokens/jcpenney.css';
import renderMiniPDP from '../../../helper/renderMiniPDP';
import marketingAction from '../../../actions/MarketingAction';
import FeatureShops from './FeatureShops';

/* istanbul ignore next */
function Marketing({
    actions,
    cmsData,
    irisData,
    location,
    history,
    irisFallbackPreferences,
    irisFallbackEnabled,
    disableIrisCall,
    isPreview,
    clientCall,
    shouldRender,
    enableFeatureShops,
    enableMiniPDPFeature,
    limitInitialComponentsRendered,
    deviceName,
    contentstackLivePreview,
    livePreviewScriptUrl,
    livePreviewIframeOnly,
}) {
    const [featureShopPageName, setFeatureShopPageName] = useState('');
    const { trackPageView } = useAnalytics();

    // Contentstack Live Preview: inject the standalone script in preview mode.
    useLivePreviewScript(contentstackLivePreview, livePreviewScriptUrl, livePreviewIframeOnly);

    const params = location.pathname;
    const featureShopParam = params?.includes(ProductConstants.FEATURE_SHOP);
    const isFeatureShopEligible = enableFeatureShops && featureShopParam;

    // SSR-safe CMS fetch — dispatched during render so it runs on the server's
    // first render pass (the hooks equivalent of IrisPage's UNSAFE_componentWillMount).
    // Must NOT live in useEffect, which never runs on the server and would leave
    // the page blank in SSR HTML.
    //
    // Gating mirrors IrisPage exactly so debug=noSSR and preview-always-CSR keep
    // working (platform features, not content-specific):
    //   - preview: fetch on the CLIENT only (never during SSR) so the preview
    //     container stays client-side rendered — the `!__SERVER__` guard is the
    //     equivalent of IRIS rendering Loading (not App) on the preview server.
    //   - otherwise: fetch only when the store is empty AND this render is allowed
    //     to trigger the call, like IRIS `isEmpty(irisData) && irisClientCall`.
    const cmsEmpty = !cmsData || (!cmsData.pageData && !cmsData.loading && !cmsData.error);
    const previewClientFetch = !__SERVER__ && isPreview && !isEmpty(params) && cmsEmpty;
    const standardFetch = !isPreview && cmsEmpty && clientCall && shouldRender;
    if (previewClientFetch || standardFetch) {
        actions.getCMSPageData({ url: params });
    }

    // Mount: client-only global triggerShippingHandling (touches window)
    useEffect(() => {
        window.triggerShippingHandling = () => {
            actions.getIrisTemplateData(
                params,
                true,
                undefined,
                irisFallbackEnabled,
                irisFallbackPreferences,
                RedirectAction.setRedirectUrl,
                disableIrisCall
            );
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Track page view when CMS data loads
    useEffect(() => {
        if (cmsData && cmsData.pageData) {
            const pageName = cmsData.pageData.meta_title || cmsData.pageData.page_title || 'Marketing';
            actions.getMarketingLoaded(pageName, 'promotions');
            trackPageView({ pageType: 'promotions', pageName, pageUrl: params });
        }
    }, [cmsData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clean up localStorage (non-preview only)
    useEffect(() => {
        if (!isPreview) {
            LocalStorage.removeData('irisEvergreenload', true);
        }
    }, [isPreview]);

    const pageNameAttribute = (data) => {
        setFeatureShopPageName(data);
    };

    const renderMiniPDPComponent = enableMiniPDPFeature ? renderMiniPDP() : null;

    // Gate the entire render on shouldRender, mirroring IrisPage. When debug=noSSR
    // (or any platform skip-SSR path) is active, the platform sets
    // context.shouldRender=false on the server, so we render null server-side and
    // let the client hydrate/render. Without this, CMSRenderer emits SSR markup
    // even under debug=noSSR, so the page never falls back to CSR.
    if (!shouldRender) return null;

    // === Feature Shops (URL-based, takes precedence) ===
    if (isFeatureShopEligible) {
        return (
            <Layout>
                <FeatureShops location={location} pageNameChange={pageNameAttribute} />
                {renderMiniPDPComponent}
            </Layout>
        );
    }

    // === Contentstack ===
    return (
        <Layout>
            <TokenProvider brand="jcpenney">
                <CMSRenderer
                    data={cmsData && cmsData.pageData}
                    lazyAfter={limitInitialComponentsRendered[deviceName]}
                />
            </TokenProvider>
            {renderMiniPDPComponent}
        </Layout>
    );
}

Marketing.propTypes = {
    actions: PropTypes.objectOf(PropTypes.func),
    cmsData: PropTypes.object,
    irisData: PropTypes.object,
    location: PropTypes.object,
    history: PropTypes.object,
    irisFallbackPreferences: PropTypes.object,
    irisFallbackEnabled: PropTypes.bool,
    disableIrisCall: PropTypes.bool,
    isPreview: PropTypes.bool,
    clientCall: PropTypes.bool,
    shouldRender: PropTypes.bool,
    enableFeatureShops: PropTypes.bool,
    enableMiniPDPFeature: PropTypes.bool,
    limitInitialComponentsRendered: PropTypes.object,
    deviceName: PropTypes.string,
    contentstackLivePreview: PropTypes.bool,
    livePreviewScriptUrl: PropTypes.string,
    livePreviewIframeOnly: PropTypes.bool,
};

Marketing.defaultProps = {
    actions: {},
    cmsData: null,
    irisData: {},
    location: {},
    history: {},
    irisFallbackPreferences: {},
    irisFallbackEnabled: false,
    disableIrisCall: false,
    isPreview: false,
    clientCall: true,
    shouldRender: true,
    enableFeatureShops: false,
    enableMiniPDPFeature: true,
    limitInitialComponentsRendered: { mobile: 8, desktop: 9, tablet: 8 },
    deviceName: 'mobile',
    contentstackLivePreview: false,
    livePreviewScriptUrl: '',
    livePreviewIframeOnly: true,
};

const mapStateToProps = ({ cmsData, irisData, context }) => {
    const featureFlags = get(context, 'featureFlags', {});
    const preferences = get(context, 'preferences', {});

    return {
        cmsData,
        irisData,
        isPreview: get(context, 'isPreview', false),
        shouldRender: selectShouldRender({ context }),
        irisFallbackPreferences: get(preferences, 'irisfallbackPreferences', {}),
        irisFallbackEnabled: featureFlags.irisFallbackEnabled || false,
        disableIrisCall: featureFlags.disableIrisCall || false,
        enableFeatureShops: featureFlags.enableFeatureShops || false,
        enableMiniPDPFeature: featureFlags.enableMiniPDPFeature !== false,
        limitInitialComponentsRendered: get(
            preferences,
            'renderingStrategy.marketing.limitInitialComponentsRendered',
            { mobile: 8, desktop: 9, tablet: 8 }
        ),
        deviceName: get(context, 'deviceType.deviceName', 'mobile'),
        // Live preview runs only when explicitly enabled AND in preview mode.
        contentstackLivePreview:
            featureFlags.enableLivePreview === true && get(context, 'isPreview', false),
        // CDN URL for the standalone live-preview script (configurable per env)
        livePreviewScriptUrl: get(preferences, 'contentstack.livePreviewScriptUrl', ''),
        // Restrict live preview to the iframe by default; false = allow any preview context.
        livePreviewIframeOnly: featureFlags.enableAllowPreviewIframeOnly !== false,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        { ...marketingAction, ...IrisActions, ...ContentstackActions },
        dispatch
    ),
});

export default withReducerSaga(
    [ContentstackReducer],
    [ContentstackSaga]
)(withRouter(connect(mapStateToProps, mapDispatchToProps)(Marketing)));
